import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  PageBody,
  TableActionsMenu,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { listCohorts } from '@/lib/cohorts.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import { formatDate, formatDateTime } from '@/utils/dateUtils.ts';

export const Route = createFileRoute('/_app/cohorts/')({
  staticData: { orgScope: 'required' },
  component: CohortsPage,
});

const columns: DataTableColumn<CohortWithOrganisation>[] = [
  { key: 'title', header: 'Title', sortable: true },
  {
    key: 'track',
    header: 'Track',
    sortable: true,
    render: (cohort) => cohort.track?.title ?? '—',
  },
  {
    key: 'start_at',
    header: 'Start Date',
    hideBelow: 'mobile',
    sortable: true,
    render: (cohort) => formatDate(cohort.start_at),
  },
  {
    key: 'created_at',
    header: 'Created At',
    hideBelow: 'mobile',
    sortable: true,
    render: (cohort) => formatDateTime(cohort.created_at),
  },
  {
    key: 'updated_at',
    header: 'Updated At',
    hideBelow: 'mobile',
    sortable: true,
    render: (cohort) => formatDateTime(cohort.updated_at),
  },
];

/*
 * Every cohort, a page at a time.
 *
 * Server-side paging, as it was: the console lists across organisations and the
 * count grows with every customer, so the page number is a request rather than
 * a slice of something already fetched.
 */
export function CohortsPage() {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'title', direction: 'asc' });
  /* The organisation the console is being looked through, as the Vue view did. */
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id ?? null;

  const { data, isPending, isError } = useQuery({
    queryKey: ['cohorts', 'list', organisationId, page, sort],
    queryFn: () =>
      listCohorts({ page, organisationId, sortBy: sort.key, sortOrder: sort.direction }),
  });

  return (
    <>
      <PageHeader title='Cohorts' />
      <PageBody className='flex flex-col gap-5'>
        <p className='text-base text-ink-muted'>
          Manage the cohorts of your organisation
        </p>

        <DataTable<CohortWithOrganisation>
          label='Cohorts'
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(cohort) => cohort.id}
          loading={isPending}
          error={isError ? 'Failed to load cohorts' : undefined}
          emptyState={
            <EmptyState
              title='No cohorts yet'
              description='A cohort groups the people who go through a track together.'
            />
          }
          toolbar={
            <Link to='/cohorts/create' className={buttonVariants({ size: 'sm' })}>
              Create
            </Link>
          }
          rowHref={(cohort) => `/cohorts/${cohort.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          pagination={{
            page: data?.meta.currentPage ?? page,
            pageSize: PAGE_SIZE,
            total: data?.meta.totalItems ?? 0,
          }}
          onPageChange={setPage}
          actions={(cohort) => (
            <TableActionsMenu
              label={`Actions for ${cohort.title}`}
              items={[
                {
                  label: 'Edit',
                  icon: <Pencil aria-hidden='true' />,
                  onSelect: () =>
                    void router.navigate({
                      to: '/cohorts/$id/edit',
                      params: { id: cohort.id },
                    }),
                },
              ]}
            />
          )}
        />
      </PageBody>
    </>
  );
}
